import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield, Eye, Lock, Users, Mail, FileText } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-primary-light rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-4xl font-bold text-secondary mb-4">Privacy Policy</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Your privacy is important to us. Learn how we collect, use, and protect your information.
          </p>
        </div>

        {/* Effective Date */}
        <div className="mb-8">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="py-4">
              <p className="text-blue-800 font-medium text-center">
                <strong>Effective Date:</strong> July 14, 2025
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="space-y-8">
          {/* Introduction */}
          <Card>
            <CardContent className="py-6">
              <p className="text-gray-700 leading-relaxed">
                UdyogaPramoda.org ("we", "our", or "us") is committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
                when you interact with our services, including our website, mobile applications, and any 
                other platforms we use to provide support to job aspirants and connect them with mentors 
                and training opportunities.
              </p>
            </CardContent>
          </Card>

          {/* 1. Information We Collect */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-xl font-bold text-secondary">
                <FileText className="w-6 h-6 mr-3 text-primary" />
                1. Information We Collect
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="font-semibold text-secondary mb-2">Personal Information:</h4>
                <p className="text-gray-700">
                  Name, email address, phone number, date of birth, location, education background, 
                  employment history, and other information you provide when registering or participating 
                  in our programs.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-secondary mb-2">Usage Information:</h4>
                <p className="text-gray-700">
                  Information about your interaction with our website or app (e.g., IP address, browser type, pages visited).
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-secondary mb-2">Mentor & Aspirant Communications:</h4>
                <p className="text-gray-700">
                  Any communication between you and mentors, trainers, or our team.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 2. How We Use Your Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-xl font-bold text-secondary">
                <Eye className="w-6 h-6 mr-3 text-primary" />
                2. How We Use Your Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4">We use the information to:</p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Connect you with mentors and potential employers
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Provide training and development resources
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Communicate updates and opportunities
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Improve our services
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-primary rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Maintain safety and integrity of our community
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* 3. Sharing Your Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-xl font-bold text-secondary">
                <Users className="w-6 h-6 mr-3 text-purple-600" />
                3. Sharing Your Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4">
                <strong>We do not sell your information.</strong> We may share your information with:
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Mentors and training partners
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Employers or recruiters, with your consent
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Service providers who help us operate our systems
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  When required by law or to protect legal rights
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* 4. Data Security */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-xl font-bold text-secondary">
                <Lock className="w-6 h-6 mr-3 text-red-600" />
                4. Data Security
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">
                We implement industry-standard security measures to protect your data. However, 
                no method of transmission or storage is 100% secure.
              </p>
            </CardContent>
          </Card>

          {/* 5. Your Rights */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-xl font-bold text-secondary">
                <Shield className="w-6 h-6 mr-3 text-indigo-600" />
                5. Your Rights
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-4">You have the right to:</p>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Access or update your personal data
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Request deletion of your data
                </li>
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-indigo-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  Withdraw consent where applicable
                </li>
              </ul>
              <p className="text-gray-700">
                To exercise these rights, contact us at{" "}
                <a 
                  href="mailto:info@udyogapramoda.org" 
                  className="text-primary hover:text-blue-700 font-medium"
                >
                  info@udyogapramoda.org
                </a>
              </p>
            </CardContent>
          </Card>

          {/* 6. Children's Privacy */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-bold text-secondary">
                6. Children's Privacy
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">
                Our services are not intended for individuals under 16 years of age.
              </p>
            </CardContent>
          </Card>

          {/* 7. Changes to This Policy */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-bold text-secondary">
                7. Changes to This Policy
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">
                We may update this Privacy Policy from time to time. Changes will be posted with a new "Effective Date."
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Contact Information */}
        <div className="mt-12">
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="py-8 text-center">
              <Mail className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-bold text-secondary mb-2">
                Questions About Our Privacy Policy?
              </h3>
              <p className="text-gray-600 mb-4">
                If you have any questions or concerns about this Privacy Policy, please contact us.
              </p>
              <a 
                href="mailto:info@udyogapramoda.org?subject=Privacy%20Policy%20Question"
                className="inline-flex items-center px-6 py-3 bg-primary text-white font-medium rounded-lg hover:bg-primary-dark transition-colors"
              >
                <Mail className="w-5 h-5 mr-2" />
                Contact Us
              </a>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
} 